import { formatDiscountPct } from './formatters';
import type { PolicySummaryState } from '$/types/travelInsurance';

/**
 * Editor-facing purchase tokens for the TI purchase-success page (CMS-1794).
 * An editor writes these literals inside a Progress Stepper greeting (e.g.
 * "Congrats {First Name}, you are now covered!"); they are replaced client-side
 * with the resolved purchase values. Extend this list to add a token.
 */
const POLICY_SUMMARY_TOKENS = [
  '{First Name}',
  '{Discount}',
  '{Brand}',
] as const;

const tokenValues = (
  state: PolicySummaryState | undefined,
): Record<(typeof POLICY_SUMMARY_TOKENS)[number], string> => ({
  '{First Name}': state?.firstName ?? '',
  // Same discount formatting as the Policy Summary Card row (single source).
  '{Discount}': state?.data
    ? (formatDiscountPct(state.data.basePremium, state.data.collectionAmount) ??
      '')
    : '',
  '{Brand}': state?.data?.carrierName ?? '',
});

/**
 * Replace the known tokens in `text` with values from the resolved purchase
 * state. When `state` is undefined (purchase not resolved yet), tokens collapse
 * to empty strings - so SSR/first-client render agree and never flash the raw
 * `{First Name}` literal; the real values fill in after hydration.
 */
export const replacePolicySummaryTokens = (
  text: string | undefined,
  state: PolicySummaryState | undefined,
): string | undefined => {
  if (!text) {
    return text;
  }
  const values = tokenValues(state);
  return POLICY_SUMMARY_TOKENS.reduce(
    (acc, token) => acc.split(token).join(values[token]),
    text,
  );
};
